import React, { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";

export default function ImageLeaflet({ latestSurvey, apiBase }) {
  const map = useMap();
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Store marker instances directly to handle external toggling efficiently
  const markersRef = useRef({}); 
  const layerGroupRef = useRef(null);

  // --- 1. Helper: Image Icon Generator (Same as Cesium Ref) ---
  const makeImageDataUrl = (size = 42, circleFill = "#ffffff", iconColor = "#222") => {
    const radius = size / 2;
    const strokeW = Math.max(1, size * 0.06);
    const pad = size * 0.24;
    const frameX = pad;
    const frameY = pad;
    const frameW = size - pad * 2;
    const frameH = size - pad * 2;

    const mountainLeft = frameX + frameW * 0.12;
    const mountainBaseY = frameY + frameH * 0.82;
    const mountainPeak1X = frameX + frameW * 0.38;
    const mountainPeak1Y = frameY + frameH * 0.40;
    const mountainMidX = frameX + frameW * 0.56;
    const mountainMidY = frameY + frameH * 0.62;
    const mountainPeak2X = frameX + frameW * 0.74;
    const mountainPeak2Y = frameY + frameH * 0.34;
    const mountainRightX = frameX + frameW * 0.88;

    const sunCX = frameX + frameW * 0.74;
    const sunCY = frameY + frameH * 0.28;
    const sunR = Math.max(1.4, size * 0.045);

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs>
          <filter id="s" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0.8" stdDeviation="1" flood-color="#000" flood-opacity="0.12"/>
          </filter>
        </defs>
        <g filter="url(#s)">
          <circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${circleFill}" stroke="#e6e6e6" stroke-width="1" />
        </g>
        <g transform="translate(0,0)" fill="none" stroke="${iconColor}" stroke-linecap="round" stroke-linejoin="round" stroke-width="${strokeW}">
          <rect x="${frameX}" y="${frameY}" width="${frameW}" height="${frameH}" rx="${Math.max(1, size * 0.03)}" fill="none" />
        </g>
        <g transform="translate(0,0)" fill="${iconColor}">
          <path d="M ${mountainLeft} ${mountainBaseY} L ${mountainPeak1X} ${mountainPeak1Y} L ${mountainMidX} ${mountainMidY} L ${mountainPeak2X} ${mountainPeak2Y} L ${mountainRightX} ${mountainBaseY} Z" />
          <path d="M ${mountainPeak1X + frameW * 0.05} ${mountainPeak1Y + frameH * 0.10} L ${mountainPeak1X + frameW * 0.10} ${mountainPeak1Y - frameH * 0.03} L ${mountainPeak1X + frameW * 0.18} ${mountainPeak1Y + frameH * 0.12} Z" fill="${circleFill}" />
          <circle cx="${sunCX}" cy="${sunCY}" r="${sunR}" />
        </g>
      </svg>
    `;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // --- 2. Fetch Images (Logic ported from Cesium Ref) ---
  useEffect(() => {
    if (!latestSurvey || !apiBase) return;

    const fetchImages = async () => {
      try {
        const loc = latestSurvey.location_name || latestSurvey.name;
        const sv = latestSurvey.survey_time || latestSurvey.survey_date;

        const response = await axios.get(`${apiBase}/ground_img/discovery`, {
          params: { location_name: loc, survey_time: sv },
        });

        if (response.data && response.data.found) {
          const found = response.data.found;

          const p1 = loc;
          const p2 = sv && sv.includes("T") ? sv.split("T")[0] : sv;
          const slugify = (s) => (s ? s.toString().replace(/[^a-z0-9]/gi, "-").toLowerCase() : "");

          const processedImages = found.map((img) => {
            const meta = img.file?.metadata || {};
            const layerName = img.layer || "images";
            const fileName = img.itemname;
            const id = `groundimg-${layerName}-${fileName}`; 

            const locSlug = slugify(p1);
            const svSlug = slugify(p2);
            let imageUrl = `${apiBase}/ground_img/${locSlug}/${svSlug}`;
            if (img.layer) imageUrl += `/${img.layer}`;
            imageUrl += `/${img.itemname}`;

            return {
              ...img,
              uniqueId: id,
              fullUrl: imageUrl,
              meta: {
                ...meta,
                location_name: p1,
                survey_time: p2,
                isAutoDiscovered: true,
                layer: img.layer,
              }
            };
          });

          setImages(processedImages);

          // Dispatch Event for Sidebar
          const layersResolved = processedImages.map((img) => ({
            id: img.uniqueId,
            title: img.itemname,
            name: img.itemname,
            url: img.fullUrl,
            visible: true,
            opacity: 1.0,
            meta: img.meta,
          }));

          window.dispatchEvent(
            new CustomEvent("ground-images-discovered", {
              detail: {
                location: p1,
                survey_time: p2,
                full_layers: layersResolved,
              },
            })
          );
        } else {
          setImages([]);
        }
      } catch (err) {
        console.error("[ImageLeaflet] Error loading images:", err);
      }
    };

    fetchImages();
  }, [latestSurvey, apiBase]);

  // --- 3. Plot Markers on Leaflet ---
  useEffect(() => {
    if (!map || !images.length) return;

    // A. Cleanup previous markers
    if (layerGroupRef.current) {
      layerGroupRef.current.clearLayers();
    } else {
      layerGroupRef.current = L.layerGroup().addTo(map);
    }
    markersRef.current = {}; // Reset ref store

    const iconUrl = makeImageDataUrl(42, "#ffffff", "#222");
    
    // Create Leaflet Icon
    const customIcon = L.icon({
      iconUrl: iconUrl,
      iconSize: [42, 42],
      iconAnchor: [21, 42], // Center X, Bottom Y (matches VerticalOrigin.BOTTOM)
      popupAnchor: [0, -42]
    });

    const latLngs = [];

    images.forEach((img) => {
      const meta = img.meta || {};
      if (meta.latitude && meta.longitude) {
        const lat = parseFloat(meta.latitude);
        const lon = parseFloat(meta.longitude);

        if (!isNaN(lat) && !isNaN(lon)) {
          // B. Create Marker
          const marker = L.marker([lat, lon], {
            icon: customIcon,
            title: img.itemname,
            zIndexOffset: 1000 // Ensure images sit on top
          });

          // C. Attach Data & Click Handler
          marker.on('click', () => {
             setSelectedImage({
               name: img.itemname,
               url: img.fullUrl,
               meta: img.meta
             });
          });

          // D. Add to Map and Store Ref
          marker.addTo(layerGroupRef.current);
          markersRef.current[img.uniqueId] = marker;
          
          latLngs.push([lat, lon]);
        }
      }
    });

    // E. Auto Zoom
    if (latLngs.length > 0) {
      const bounds = L.latLngBounds(latLngs);
      // Add slight padding so icons aren't on the edge
      map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 18, duration: 1.5 });
    }

    return () => {
      if (layerGroupRef.current) layerGroupRef.current.clearLayers();
    };
  }, [map, images]);

  // --- 4. Listen for Toggle Events (Sidebar) ---
  useEffect(() => {
    if (!map) return;

    const handleToggle = (e) => {
      const { id, visible } = e.detail || {};
      
      // Check if this is one of our managed markers
      if (id && id.startsWith("groundimg-") && markersRef.current[id]) {
        const marker = markersRef.current[id];
        
        if (visible) {
          if (!layerGroupRef.current.hasLayer(marker)) {
            layerGroupRef.current.addLayer(marker);
          }
        } else {
          if (layerGroupRef.current.hasLayer(marker)) {
            layerGroupRef.current.removeLayer(marker);
          }
        }
      }
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
    };
  }, [map]);

  // --- 5. Modal Render ---
  if (!selectedImage) return null;

  return (
    <div style={modalOverlayStyle} onClick={() => setSelectedImage(null)}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>{selectedImage.name}</span>
          <button style={closeButtonStyle} onClick={() => setSelectedImage(null)}>×</button>
        </div>
        <div style={imageContainerStyle}>
          <img
            src={selectedImage.url}
            alt="Ground Survey"
            style={{ maxWidth: "100%", maxHeight: "60vh", display: "block" }}
          />
        </div>
        <div style={metaStyle}>
          {selectedImage.meta.mtime && <div>Date: {new Date(selectedImage.meta.mtime).toLocaleString()}</div>}
          <div>[{selectedImage.meta.latitude?.toFixed(5)}, {selectedImage.meta.longitude?.toFixed(5)}]</div>
        </div>
      </div>
    </div>
  );
}

// --- Styles (Identical to Reference) ---
const modalOverlayStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0,0,0,0.7)", zIndex: 3000,
  display: "flex", justifyContent: "center", alignItems: "center",
};
const modalContentStyle = {
  backgroundColor: "white", padding: "15px", borderRadius: "8px",
  maxWidth: "90%", maxHeight: "90%", display: "flex", flexDirection: "column", gap: "10px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
};
const headerStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  borderBottom: "1px solid #eee", paddingBottom: "8px", fontWeight: "bold", color: "#333"
};
const closeButtonStyle = {
  background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666"
};
const imageContainerStyle = {
  display: "flex", justifyContent: "center", alignItems: "center",
  backgroundColor: "#f0f0f0", minHeight: "200px", borderRadius: "4px"
};
const metaStyle = {
  fontSize: "12px", color: "#666", display: "flex", justifyContent: "space-between", flexWrap: "wrap"
};